import { useEffect } from 'react';

import { EventEmitter } from '../utils';

let lastScrollTop = 0;

export interface ScrollEvent {
	scrollTop: number;
	target?: HTMLElement | 'timeline';
}

export const scrollEmitter = new EventEmitter<ScrollEvent>();

export function notifyNeedToScroll({
	target,
	scrollTop,
	immediate
}: ScrollEvent & {
	immediate?: boolean;
}) {
	if (lastScrollTop === scrollTop) {
		return;
	}

	lastScrollTop = scrollTop;

	if (immediate) {
		scrollEmitter.emit({ scrollTop: lastScrollTop, target: target });
		return;
	}

	// Run in next animation so it will be smooth
	requestAnimationFrame(() => {
		scrollEmitter.emit({ scrollTop: lastScrollTop, target: target });
	});
}

const SCROLL_KEY_DELTA = 40;

const keydownEventAdapter = ({ code }: KeyboardEvent) => {
	let deltaY = 0;

	switch (code) {
		case 'ArrowUp':
			deltaY = -SCROLL_KEY_DELTA;
			break;
		case 'ArrowDown':
			deltaY = SCROLL_KEY_DELTA;
			break;
		case 'Space':
			deltaY = SCROLL_KEY_DELTA * 2;
			break;
	}

	return deltaY;
};

const shouldScroll = ({ code }: KeyboardEvent) => ['Space', 'ArrowUp', 'ArrowDown'].includes(code);

const useScroll = (innerScroll?: HTMLElement | null, container?: HTMLElement | null, columnIndex?: number) => {
	useEffect(() => {
		let elementHeight = 0;
		let containerHeight = 0;
		let maxScroll = 0;

		const onWheel = (event: WheelEvent) => {
			event.preventDefault();

			if (!elementHeight) {
				elementHeight = innerScroll?.offsetHeight || 0;
				maxScroll = Math.max(elementHeight - containerHeight, 0);
			}
			if (!containerHeight) {
				containerHeight = container?.offsetHeight || 0;
				maxScroll = Math.max(elementHeight - containerHeight, 0);
			}

			// If the number of items is less than the container height, there is no need to scroll
			if (maxScroll === 0) {
				return;
			}

			const target = event.currentTarget as HTMLDivElement;

			const deltaY = event.deltaY;
			const targetScrollTop = Math.min(Math.max(lastScrollTop + deltaY, 0), maxScroll);

			notifyNeedToScroll({ target, scrollTop: targetScrollTop, immediate: true });
		};

		container?.addEventListener('wheel', onWheel);

		const scroll = ({ scrollTop }: ScrollEvent) => {
			if (container) container.scrollTop = scrollTop;
		};

		scrollEmitter.on(scroll);

		const onScrollBar = (e: Event) => {
			e.preventDefault();

			const newScrollTop = (e.target as Element).scrollTop;
			if (lastScrollTop !== newScrollTop) {
				const target = e.currentTarget as HTMLDivElement;

				notifyNeedToScroll({ target, scrollTop: newScrollTop });
			}
		};

		container?.addEventListener('scroll', onScrollBar);

		let resizeObserver: ResizeObserver;

		if (innerScroll || container) {
			resizeObserver = new ResizeObserver(entries => {
				elementHeight =
					entries.find(item => item.target === innerScroll)?.contentRect.height ||
					innerScroll?.offsetHeight ||
					0;
				containerHeight =
					entries.find(item => item.target === container)?.contentRect.height || container?.offsetHeight || 0;
				maxScroll = Math.max(elementHeight - containerHeight, 0);
				const targetScroll = Math.min(Math.max(lastScrollTop, 0), maxScroll);

				notifyNeedToScroll({ target: innerScroll || undefined, scrollTop: targetScroll, immediate: true });
			});

			if (innerScroll) resizeObserver.observe(innerScroll);
			if (container) resizeObserver.observe(container);
		}

		// syncing scroll with new added column
		if (container && lastScrollTop !== container.scrollTop) {
			scroll({ scrollTop: lastScrollTop });
		}

		const smoothScroll = (deltaY: number, target?: HTMLElement) => {
			const recurse = (h?: number) => {
				const i = h || 0;
				if (deltaY > 0 ? i < deltaY : i > deltaY) {
					setTimeout(() => {
						const newScrollTop = Math.min(Math.max(lastScrollTop + i, 0), maxScroll);
						notifyNeedToScroll({ target, scrollTop: newScrollTop, immediate: true });
						recurse(i + deltaY / 10);
					}, 0);
				}
			};

			recurse();
		};

		const onKeydown = (event: KeyboardEvent) => {
			if (!shouldScroll(event)) {
				return;
			}
			event.preventDefault();

			const target = event.currentTarget as HTMLDivElement;
			const deltaY = keydownEventAdapter(event);

			smoothScroll(deltaY, target);
		};

		container?.addEventListener('keydown', onKeydown);

		return () => {
			innerScroll?.removeEventListener('wheel', onWheel);
			container?.removeEventListener('scroll', onScrollBar);
			container?.removeEventListener('keydown', onKeydown);
			scrollEmitter.off(scroll);

			if (innerScroll) resizeObserver.unobserve(innerScroll);
			if (container) resizeObserver.unobserve(container);
			resizeObserver?.disconnect();
		};
	}, [container, innerScroll, columnIndex]);
};

export default useScroll;
